import _ from 'lodash';
import { RandomInt } from './Random';

declare global {
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface Array<T> {
		/** @returns The number of values matching predicate */
		countWhere(predicate: (value: T) => boolean): number;
		empty(): boolean;
		/** @returns The first element for which the async predicate returns true. Undefined if no matches. */
		findAsync(predicate: (value: T) => Promise<boolean>): Promise<T | undefined>;
		/** Force the length of the array by removing or duplicating elements from the end of the array */
		forceLength(length: number): T[];
		/** Compares two arrays for equality regardless of element order. */
		isEqualUnordered(other: T[]): boolean;
		last(): T | undefined;
		getSafe(index: number, errorMessage?: string): T;
		randomElement(): T;
		/** @returns A new array with `value` added if it was not previously present, or removed otherwise */
		withToggledValue(value: T): T[];
		uniqueValues(): T[];
	}
}

Array.prototype.countWhere = function <T>(predicate: (value: T) => boolean): number {
	return this.reduce(
		(count, value) => predicate(value) ? (count + 1) : count,
		0);
};

Array.prototype.empty = function (): boolean {
	return this.length === 0;
};

Array.prototype.findAsync = async function <T>(predicate: (value: T) => Promise<boolean>): Promise<T | undefined> {
	for (const element of this) {
		if (await predicate(element)) {
			return element;
		}
	}
	return undefined;
};

Array.prototype.forceLength = function <T>(length: number): T[] {
	const delta = length - this.length;
	if (delta > 0) {
		const result = [...this];
		while (!this.empty() && (result.length < length)) {
			const appendCount = Math.min(length - result.length, this.length);
			result.push(...this.slice(0, appendCount));
		}
		return result;
	} else if (delta < 0) {
		return this.slice(0, this.length + delta);
	} else {
		return [...this];
	}
};

Array.prototype.isEqualUnordered = function <T>(other: T[]): boolean {
	const thisSorted = [...this].sort();
	const otherSorted = [...other].sort();
	return _.isEqual(thisSorted, otherSorted);
};

Array.prototype.last = function <T>(): T | undefined {
	return (this.length > 0) ? this[this.length - 1] : undefined;
};

Array.prototype.getSafe = function <T>(index: number, errorMessage?: string): T {
	const result = this[index];
	if (result !== undefined) {
		return result;
	} else {
		throw new Error(errorMessage ?? `Failed to look up element ${index} in array of length ${this.length}`);
	}
};

Array.prototype.randomElement = function <T>(): T {
	const index = RandomInt.inRange(0, this.length);
	return this[index];
};

Array.prototype.withToggledValue = function <T>(value: T): T[] {
	return this.includes(value)
		? this.filter(b => b !== value)
		: [...this, value];
};

Array.prototype.uniqueValues = function <T>(): T[] {
	return [...new Set(this)];
};

export function* reverseKeys<T>(arr: ArrayLike<T>) {
	let key = arr.length - 1;

	while (key >= 0) {
		yield key;
		key -= 1;
	}
}

export function* reverseValues<T>(arr: T[]) {
	for (const key of reverseKeys(arr)) {
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const next: T = arr[key]!;
		yield next;
	}
}

export function mapFromArray<T extends object, K extends keyof T>(arr: T[], key: K)
	: Record<T[K] extends PropertyKey ? T[K] : never, T> {
	return Object.fromEntries(
		arr.filter(value => value[key] !== undefined)
			.map(value => [value[key], value])
	);
}
